import { useCallback, useMemo, type FC, type MouseEvent } from "react";
import { FileText, Globe, Mail, Paperclip } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { MessageBody, cn } from "@ando/ui";

/**
 * CanvasReader — long-form reading surface for a compiled Knowledge Canvas.
 *
 * Wraps ``MessageBody`` (the shared markdown renderer used by chat) with
 * two canvas-specific concerns:
 *
 *   1. **Numbered citations.** The compile pipeline emits source markers
 *      inline as ``[Source: file_a.pdf, file_b.pdf]`` literals. Rendered
 *      raw they bloat every sentence and break the reading rhythm. We
 *      pre-process the markdown into Wikipedia/Perplexity-style numbered
 *      chips (``[1]`` … ``[N]``) and surface the deduped source list in
 *      a single ``Fonti`` footer the user can scan once.
 *
 *   2. **Reader-grade typography.** The class ``prose-canvas`` (defined in
 *      ``packages/ui/src/styles.css``) gives ``MessageBody`` h1/h2/h3
 *      hierarchy, generous leading, and chip styling — none of which
 *      ``msg-body`` carries on its own because chat answers are tuned
 *      for compactness.
 *
 * Robustness: the regex matches ``[Source: …]`` non-greedily up to ``]``,
 * so colons inside source names (email subjects like ``Re: Foo``) are
 * preserved. Comma-splitting source names is naive (a comma inside a
 * source name would split it); acceptable today because the BE emits
 * filenames + email subjects, neither of which contains commas in the
 * current corpus. If that changes, switch to a non-comma separator at
 * the compile step.
 */
export interface CanvasReaderProps {
  /** Compiled canvas body — raw markdown from ``Canvas.content_md``. */
  markdown: string;
  /** Optional className appended to the outer reader wrapper. */
  className?: string;
}

interface PreparedCanvas {
  /** Markdown with ``[Source: …]`` rewritten into ``[\[N\]](#cite-N)`` links. */
  body: string;
  /** Deduped, 1-indexed list of source names in first-mention order. */
  sources: readonly string[];
}

/**
 * Walk the markdown body, dedup source names in first-mention order, and
 * rewrite each ``[Source: …]`` marker into a sequence of numbered chip
 * links. Pure: never throws, returns the original markdown unchanged when
 * no markers are present.
 */
export function prepareCanvasMarkdown(markdown: string): PreparedCanvas {
  const sources: string[] = [];
  const indexByName = new Map<string, number>();

  const body = markdown.replace(/\[Source:\s*([^\]]+)\]/g, (_, raw: string) => {
    const names = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (names.length === 0) return "";
    const chips = names.map((name) => {
      let idx = indexByName.get(name);
      if (idx == null) {
        idx = sources.length + 1;
        indexByName.set(name, idx);
        sources.push(name);
      }
      // Escape the brackets so ReactMarkdown renders literal "[N]" link
      // text instead of consuming them as another link delimiter.
      return `[\\[${idx}\\]](#cite-${idx})`;
    });
    return chips.join("");
  });

  return { body, sources };
}

export const CanvasReader: FC<CanvasReaderProps> = ({ markdown, className }) => {
  const { body, sources } = useMemo(
    () => prepareCanvasMarkdown(markdown),
    [markdown],
  );

  // Intercept clicks on the synthetic ``#cite-N`` chips so the in-page
  // anchor scroll is smooth + centred — the default browser jump is
  // instant and lands the entry flush with the viewport top, which the
  // sticky page chrome would then overlap. ``:target`` highlight still
  // fires because we let the URL hash update via ``history.pushState``.
  const handleProseClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement | null)?.closest?.(
      'a[href^="#cite-"]',
    ) as HTMLAnchorElement | null;
    if (!anchor) return;
    const id = anchor.getAttribute("href")?.slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    // Push the hash so ``:target`` matches and the URL is shareable.
    window.history.pushState(null, "", `#${id}`);
    target.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div onClick={handleProseClick}>
        <MessageBody markdown={body} className="prose-canvas" />
      </div>
      {sources.length > 0 ? <CanvasSourcesFooter sources={sources} /> : null}
    </div>
  );
};
CanvasReader.displayName = "CanvasReader";

/**
 * Pick a Lucide icon that matches the kind of source the BE cited. Done
 * by suffix / structure inspection because the BE today emits free-form
 * names (filenames, email subjects, URLs) — no explicit ``type`` field.
 * Fallback ``Paperclip`` keeps the row visually consistent for unknown
 * shapes (Salesforce records, ad-hoc strings, …).
 */
function iconForSourceName(name: string): typeof FileText {
  const s = name.trim();
  if (/^https?:\/\//i.test(s)) return Globe;
  if (/^Re:\s|^Fwd:\s|@/i.test(s)) return Mail;
  if (/\.(pdf|docx?|xlsx?|pptx?|txt|md|csv|json)$/i.test(s)) return FileText;
  return Paperclip;
}

/**
 * Footer list — the canonical source register the inline chips link into.
 * Each entry has a stable ``id="cite-N"`` so the in-text chip's
 * ``href="#cite-N"`` scrolls (and pulses, via the ``:target`` rule on
 * ``.canvas-source-entry`` in ``styles.css``) to the exact entry. Two
 * columns on wide screens to keep the footer scannable when the source
 * set grows beyond ~6.
 */
const CanvasSourcesFooter: FC<{ sources: readonly string[] }> = ({ sources }) => (
  <aside
    aria-labelledby="canvas-sources-heading"
    className="rounded-xl border border-hairline bg-white/55 p-4"
  >
    <h2
      id="canvas-sources-heading"
      className="mb-2 font-semibold text-[10.5px] text-ink-3 uppercase tracking-[0.08em]"
    >
      <Trans>Fonti</Trans> · {sources.length}
    </h2>
    <ol className="grid grid-cols-1 gap-x-6 gap-y-1 md:grid-cols-2">
      {sources.map((name, i) => {
        const idx = i + 1;
        const Icon = iconForSourceName(name);
        return (
          <li
            key={`${idx}-${name}`}
            id={`cite-${idx}`}
            className="canvas-source-entry -mx-1.5 flex items-center gap-2 px-1.5 py-0.5 text-[12.5px] text-ink-2"
          >
            <span className="shrink-0 font-semibold text-ando-orange tabular-nums">
              [{idx}]
            </span>
            <Icon
              size={13}
              strokeWidth={1.75}
              className="shrink-0 text-ink-3"
              aria-hidden
            />
            <span className="truncate">{name}</span>
          </li>
        );
      })}
    </ol>
  </aside>
);
